// card-inline-variants.js
// Sélection de variante inline sur les tuiles collection (desktop ≥ sm / 700px).
// Stratégie : pré-rendu Liquid (slots prix par variante dans product-card-cart.liquid)
// + bascule de visibilité en JS. Aucun fetch réseau.
// Réutilise les exports du bundle `theme` via l'importmap déclarée dans layout/theme.liquid.
import { createMediaImg, generateSrcset, matchesMediaQuery } from 'theme';

const WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];

// Remplace (ou crée / supprime) l'image de hover .product-card__image--secondary pour
// refléter la variante sélectionnée. hoverSrc est une URL Shopify (avec ?width=…) que
// generateSrcset accepte directement — robuste quel que soit le type d'image source
// (média produit OU metafield lifestyle).
function swapHoverImage(card, hoverSrc, hoverAlt) {
  let secondary = card.querySelector('.product-card__image--secondary');

  // Pas d'image de hover distincte pour cette variante : on retire l'élément pour
  // désactiver l'effet (la règle CSS :has(.--secondary):hover ne matchera plus).
  if (!hoverSrc) {
    if (secondary) secondary.remove();
    return;
  }

  if (!secondary) {
    const media = card.querySelector('.product-card__media');
    const primary = card.querySelector('.product-card__image--primary');
    if (!media || !primary) return;
    secondary = new Image();
    secondary.className = 'product-card__image product-card__image--secondary';
    secondary.loading = 'lazy';
    secondary.setAttribute('draggable', 'false');
    if (primary.sizes) secondary.sizes = primary.sizes;
    media.appendChild(secondary);
  }

  // Neutralise la promotion lazy inline (data-hover-src → src au survol, cf. theme.liquid)
  // pour qu'elle ne réécrase pas notre image au prochain hover.
  secondary.removeAttribute('data-hover-src');
  secondary.removeAttribute('data-hover-srcset');

  if (secondary.getAttribute('src') === hoverSrc) return;
  secondary.alt = hoverAlt || '';
  secondary.setAttribute('src', hoverSrc);
  secondary.srcset = generateSrcset(hoverSrc, WIDTHS);
}

// Applique une variante (par son id) à une tuile : prix, label, input panier, et —
// pour le slider thé uniquement — image + liens PDP. Le slot prix (clé = variant.id)
// est la source unique des métadonnées.
// - swapImageAndLinks : true pour le slider thé (on possède l'image + les liens) ;
//   false pour les swatches couleur (onSwatchChanged_fn du bundle s'en charge déjà).
function applyVariant(card, id, { mediaJson = null, hoverSrc = null, hoverAlt = '', swapImageAndLinks = false, activeEl = null } = {}) {
  const slot = card.querySelector(`[data-variant-price="${id}"]`);
  if (!slot || slot.dataset.variantAvailable === 'false') return;

  // 1) Prix : bascule des slots pré-rendus
  card.querySelectorAll('[data-variant-price]').forEach((s) => {
    s.hidden = s !== slot;
  });

  // 1bis) Badge promo sur l'image : bascule de la pastille de remise pré-rendue (une par
  // variante en promo, cf. product-badges.liquid contexte card). Aucune pastille = pas de
  // promo sur la variante choisie → toutes masquées.
  card.querySelectorAll('[data-variant-discount]').forEach((badge) => {
    badge.hidden = badge.getAttribute('data-variant-discount') !== id;
  });

  // 2) Label accolé au titre
  const label = card.querySelector('[data-variant-label-target]');
  if (label) label.textContent = ' – ' + (slot.dataset.variantLabel || '');

  // 3) CTA : input id + compare_at_price + état dispo du bouton
  const form = card.querySelector('product-form');
  if (form) {
    const idInput = form.querySelector('[data-variant-add-input]');
    if (idInput) idInput.value = id;

    const cmpVal = slot.dataset.variantCompareAt || '';
    const cmp = form.querySelector('input[name="properties[_compare_at_price]"]');
    const cmpReason = form.querySelector('input[name="properties[_compare_at_price_reason]"]');
    if (cmp) {
      cmp.value = cmpVal;
      cmp.disabled = !cmpVal;
    }
    if (cmpReason) cmpReason.disabled = !cmpVal;

    const submit = form.querySelector('button[type="submit"]');
    if (submit) submit.disabled = slot.dataset.variantAvailable === 'false';
  }

  // 4) Image + liens PDP (slider thé uniquement ; les swatches passent par onSwatchChanged_fn)
  if (swapImageAndLinks) {
    const primary = card.querySelector('.product-card__image--primary');
    if (primary && mediaJson) {
      try {
        const next = createMediaImg(JSON.parse(mediaJson), WIDTHS, {
          class: primary.className,
          sizes: primary.sizes,
        });
        if (next && next.src !== primary.src) primary.replaceWith(next);
      } catch (_) {
        /* media absent ou illisible : on garde l'image courante */
      }
    }
    // Image de hover (secondaire) alignée sur la variante sélectionnée
    swapHoverImage(card, hoverSrc, hoverAlt);
    if (slot.dataset.variantUrl) {
      card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        a.href = slot.dataset.variantUrl;
      });
    }
  }

  // 5) État actif des boutons (slider thé). --first-available porte l'indicateur visuel
  //    rendu côté serveur ; on le déplace avec is-selected pour ne pas garder deux bordures.
  if (activeEl) {
    card.querySelectorAll('.product-card__variant-item').forEach((el) => {
      const active = el === activeEl;
      el.classList.toggle('is-selected', active);
      el.classList.toggle('product-card__variant-item--first-available', active);
      if (el.hasAttribute('aria-pressed')) {
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    });
  }

  // 6) Side cart : rien à faire — le submit du <product-form> émet cart:change/variant:add,
  //    ce qui ouvre le cart-drawer automatiquement (cf. CartDrawer.onCartChange).
}

// Slider thé : clic sur un bouton variante. Délégation globale → couvre aussi les cartes
// Nosto injectées après coup (?view=card).
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.product-card__variant-item[data-variant-id]');
  if (!btn || btn.disabled) return;
  if (!matchesMediaQuery('sm')) return; // desktop uniquement ; mobile garde le quick-buy modal
  const card = btn.closest('product-card');
  if (!card) return;
  applyVariant(card, btn.dataset.variantId, {
    mediaJson: btn.dataset.variantMedia,
    hoverSrc: btn.dataset.variantHoverSrc,
    hoverAlt: btn.dataset.variantHoverAlt,
    swapImageAndLinks: true,
    activeEl: btn,
  });
});

// Non-thé à couleur : changement de swatch. L'image + les liens PDP sont déjà gérés par
// onSwatchChanged_fn (bundle) ; ici on greffe uniquement prix + label + input panier.
document.addEventListener('change', (event) => {
  const input = event.target.closest('input[data-variant-id]');
  if (!input) return;
  if (!matchesMediaQuery('sm')) return;
  const card = input.closest('product-card');
  if (!card) return;
  applyVariant(card, input.dataset.variantId, { swapImageAndLinks: false });
});
